/**
 * **How old a share price is, and which exchange it came from** — one
 * definition, two surfaces (VALE-1008).
 *
 * Promoted out of `SharePriceBlock.tsx`, which was its only consumer while the
 * drawers rendered that card. The drawers render `marketFigure` rows now and need
 * the same cue in their popovers and at rest, so this is the *bind to the source
 * of truth* rule applied before the second copy exists rather than after: a
 * freshness cue that said "3 days ago" on one surface and "29 Aug 2026" on the
 * other would be two answers to one question, and nothing would fail.
 *
 * Deliberately a plain module with no `'use client'` and no React import — a spec
 * builder is a pure function, and importing this must not pull a component in
 * behind it.
 */

/** The exchange Vale prices share holdings against. SRN and HIN alike are ASX-listed. */
export const SHARE_EXCHANGE = 'ASX';

export type FreshnessTone = 'fresh' | 'recent' | 'stale';

export interface Freshness {
  /** The full cue, e.g. `Share price updated 3 days ago`. */
  label: string;
  /** The same fact at badge length, e.g. `3d ago`. */
  short: string;
  tone: FreshnessTone;
}

/** Derive the "as of" cue from the real `item_info.lastPriceUpdatedAt`. */
export function getFreshness(iso: string | undefined): Freshness | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;

  const now = new Date();
  const startOfDay = (d: Date) =>
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86400000);

  if (days <= 0)
    return {
      label: 'Share price updated today',
      short: 'Today',
      tone: 'fresh'
    };
  if (days === 1)
    return {
      label: 'Share price updated yesterday',
      short: 'Yesterday',
      tone: 'fresh'
    };
  if (days <= 7)
    return {
      label: `Share price updated ${days} days ago`,
      short: `${days}d ago`,
      tone: 'recent'
    };

  const date = then.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return {
    label: `Share price updated on ${date}`,
    short: date,
    tone: 'stale'
  };
}
